/**
 * Application Sidebar
 *
 * Hover-to-expand sidebar using Aceternity UI patterns.
 * Features: Collapsible navigation sections, deals list, user profile.
 */

import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import {
  Home,
  Briefcase,
  TrendingUp,
  Users,
  Wallet,
  UserPlus,
  Settings,
  LogOut,
  MessageSquare,
  ChevronRight,
  LayoutGrid,
  FolderOpen,
  Bug,
  Sparkles,
  Sliders,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/features/auth/context/AuthContext'
import { useActiveDeals } from '@/features/deals/hooks/useDealChat'
import { BugReportModal } from '@/features/feedback'
import { useWhatsNew } from '@/features/whats-new'
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
  Logo,
  useSidebar,
} from '@/components/ui/sidebar'
import { VersionInfo } from './VersionInfo'

// ============================================
// Navigation Configuration
// ============================================

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
  showBadge?: boolean
}

// Base nav items (without dynamic state)
const baseNavItems: Omit<NavItem, 'showBadge'>[] = [
  { label: 'Dashboard', href: '/', icon: <Home className="h-5 w-5" /> },
  { label: 'Deals', href: '/deals', icon: <Briefcase className="h-5 w-5" /> },
  { label: 'Funds', href: '/funds', icon: <TrendingUp className="h-5 w-5" /> },
  { label: 'Portfolio', href: '/portfolio', icon: <Wallet className="h-5 w-5" /> },
  { label: 'Contacts', href: '/contacts', icon: <Users className="h-5 w-5" /> },
  { label: 'Team', href: '/team', icon: <UserPlus className="h-5 w-5" /> },
  { label: 'Scoring', href: '/scoring', icon: <Sliders className="h-5 w-5" /> },
  { label: 'Settings', href: '/settings', icon: <Settings className="h-5 w-5" /> },
  { label: "What's New", href: '/whats-new', icon: <Sparkles className="h-5 w-5" /> },
]

// ============================================
// Main Sidebar Export
// ============================================

type ActiveSection = 'navigation' | 'deals' | null

export function AppSidebar() {
  const { signOut, user, organization } = useAuth()
  const { data: dealsData, isLoading: dealsLoading } = useActiveDeals()
  const { showBadge } = useWhatsNew()
  // Accordion state - only one section open at a time (navigation expanded by default)
  const [activeSection, setActiveSection] = useState<ActiveSection>('navigation')
  // Bug report modal state
  const [bugReportOpen, setBugReportOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
  }

  const toggleSection = (section: ActiveSection) => {
    setActiveSection((current) => (current === section ? null : section))
  }

  const activeDeals = dealsData?.items || []

  // Compute nav items with dynamic badge state
  const navItems: NavItem[] = baseNavItems.map((item) => {
    // Add badge to What's New item if there are unseen updates
    if (item.href === '/whats-new') {
      return { ...item, showBadge }
    }
    return item
  })

  // Add Command Center link for platform admins
  if (user?.is_platform_admin) {
    navItems.push({
      label: 'Command Center',
      href: '/admin',
      icon: <LayoutGrid className="h-5 w-5" />,
    })
  }

  return (
    <Sidebar animate={false}>
      <SidebarBody className="justify-between gap-2">
        {/* Top Section */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Company name - links to dashboard */}
          <Logo
            text={organization?.name || 'SieveAI'}
            href="/"
          />

          <Separator className="my-3" />

          {/* Main Navigation - Collapsible */}
          <CollapsibleSection
            icon={<LayoutGrid className="h-5 w-5" />}
            label="Navigation"
            isOpen={activeSection === 'navigation'}
            onToggle={() => toggleSection('navigation')}
          >
            <nav className="flex flex-col gap-0.5 pl-1">
              {navItems.map((item) => (
                <SidebarLink key={item.href} link={item} />
              ))}
              {/* Bug Report - opens modal instead of navigating */}
              <BugReportButton onClick={() => setBugReportOpen(true)} />
            </nav>
          </CollapsibleSection>

          <Separator className="my-2" />

          {/* Deals Section - Collapsible, fills remaining space */}
          <CollapsibleSection
            icon={<FolderOpen className="h-5 w-5" />}
            label="Active Deals"
            isOpen={activeSection === 'deals'}
            onToggle={() => toggleSection('deals')}
            count={activeDeals.length}
            fillHeight
          >
            <DealsContent deals={activeDeals} isLoading={dealsLoading} />
          </CollapsibleSection>
        </div>

        {/* Bottom Section - User Profile */}
        <div className="shrink-0">
          {/*
            NOTE: SystemHealthIndicator removed for multi-tenancy security.
            Infrastructure metrics should not be visible to regular users.
            Will be re-implemented in admin-only dashboard.
          */}
          <UserProfile user={user} onSignOut={handleSignOut} />
          <VersionInfo />
        </div>
      </SidebarBody>

      {/* Bug Report Modal - rendered outside sidebar body to avoid animation conflicts */}
      <BugReportModal open={bugReportOpen} onOpenChange={setBugReportOpen} />
    </Sidebar>
  )
}

// Keep old export name for backwards compatibility
export { AppSidebar as Sidebar }

// ============================================
// Collapsible Section Component
// ============================================

interface CollapsibleSectionProps {
  icon: React.ReactNode
  label: string
  isOpen: boolean
  onToggle: () => void
  count?: number
  fillHeight?: boolean
  children: React.ReactNode
}

function CollapsibleSection({
  icon,
  label,
  isOpen,
  onToggle,
  count,
  fillHeight,
  children,
}: CollapsibleSectionProps) {
  const { open: sidebarOpen, animate } = useSidebar()

  return (
    <div className={cn('flex flex-col', fillHeight && isOpen && 'flex-1 min-h-0')}>
      {/* Section Header - Clickable */}
      <button
        onClick={onToggle}
        className={cn(
          'flex items-center gap-2 py-2 px-2 rounded-md transition-colors w-full shrink-0',
          'text-muted-foreground hover:bg-muted hover:text-foreground',
          'group'
        )}
        title={!sidebarOpen ? label : undefined}
      >
        <span className="shrink-0">{icon}</span>

        <motion.div
          animate={{
            display: animate ? (sidebarOpen ? 'flex' : 'none') : 'flex',
            opacity: animate ? (sidebarOpen ? 1 : 0) : 1,
          }}
          className="flex-1 flex items-center justify-between min-w-0"
        >
          <span className="text-xs font-semibold uppercase tracking-wider truncate">
            {label}
          </span>
          <div className="flex items-center gap-1">
            {count !== undefined && count > 0 && (
              <span className="text-[10px] bg-muted-foreground/20 px-1.5 py-0.5 rounded-full">
                {count}
              </span>
            )}
            <ChevronRight
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isOpen && 'rotate-90'
              )}
            />
          </div>
        </motion.div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: fillHeight ? '100%' : 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className={cn('overflow-hidden', fillHeight && 'flex-1 min-h-0')}
          >
            <div className={cn('py-1', fillHeight && 'h-full')}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ============================================
// Deals Content Component
// ============================================

interface DealsContentProps {
  deals: Array<{ id: string; company_name: string }>
  isLoading: boolean
}

function DealsContent({ deals, isLoading }: DealsContentProps) {
  const { open, animate, setOpen } = useSidebar()
  const location = useLocation()

  const handleDealClick = () => {
    // Close sidebar on mobile when deal link is clicked
    setOpen(false)
  }

  if (isLoading) {
    return (
      <div className="space-y-1 px-1">
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            className={cn('h-8', animate && !open ? 'w-8' : 'w-full')}
          />
        ))}
      </div>
    )
  }

  if (deals.length === 0) {
    return (
      <motion.p
        animate={{
          display: animate ? (open ? 'block' : 'none') : 'block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-xs text-muted-foreground px-2 py-2"
      >
        No active deals
      </motion.p>
    )
  }

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-0.5 pl-1">
        {deals.map((deal) => {
          const isActive = location.pathname === `/deals/${deal.id}`

          return (
            <Link
              key={deal.id}
              to={`/deals/${deal.id}`}
              onClick={handleDealClick}
              className={cn(
                'flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors text-sm',
                isActive
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              title={!open ? deal.company_name : undefined}
            >
              <MessageSquare className="h-4 w-4 shrink-0" />
              <motion.span
                animate={{
                  display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
                  opacity: animate ? (open ? 1 : 0) : 1,
                }}
                className="truncate"
              >
                {deal.company_name}
              </motion.span>
            </Link>
          )
        })}
      </div>
    </ScrollArea>
  )
}

// ============================================
// User Profile Component
// ============================================

interface UserProfileProps {
  user: { full_name?: string | null; email: string; role?: string | null } | null
  onSignOut: () => void
}

// ============================================
// Bug Report Button Component
// ============================================

interface BugReportButtonProps {
  onClick: () => void
}

function BugReportButton({ onClick }: BugReportButtonProps) {
  const { open, animate } = useSidebar()

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 py-2 px-2 rounded-md transition-colors w-full text-left',
        'text-muted-foreground hover:bg-muted hover:text-foreground',
        'group'
      )}
      title={!open ? 'Report a Bug' : undefined}
    >
      <Bug className="h-5 w-5 shrink-0 text-[#d97757]" />
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-sm"
      >
        Report a Bug
      </motion.span>
    </button>
  )
}

// ============================================
// User Profile Component
// ============================================

function UserProfile({ user, onSignOut }: UserProfileProps) {
  const { open, animate } = useSidebar()

  return (
    <div className="space-y-2">
      {/* User Info */}
      {user && (
        <motion.div
          animate={{
            display: animate ? (open ? 'block' : 'none') : 'block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="px-2"
        >
          <p className="text-sm font-medium truncate">{user.full_name || user.email}</p>
          {user.role && (
            <p className="text-xs text-muted-foreground truncate">{user.role}</p>
          )}
        </motion.div>
      )}

      {/* Logout Button */}
      <button
        onClick={onSignOut}
        className={cn(
          'flex items-center gap-2 py-2 px-2 rounded-md transition-colors w-full',
          'text-destructive hover:bg-destructive/10'
        )}
        title={!open ? 'Logout' : undefined}
      >
        <LogOut className="h-5 w-5 shrink-0" />
        <motion.span
          animate={{
            display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="text-sm"
        >
          Logout
        </motion.span>
      </button>

      {/* Powered by Sieve */}
      <motion.div
        animate={{
          display: animate ? (open ? 'block' : 'none') : 'block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
      >
        <Separator className="my-2" />
        <div className="px-2 space-y-1">
          <p className="text-[10px] text-muted-foreground">
            Powered by{' '}
            <a
              href="https://sieve.arceusxventures.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sieve hover:underline"
            >
              Sieve
            </a>
          </p>
          <p className="text-[10px] text-muted-foreground">
            Your Digital Analyst
          </p>
          <p className="text-[8px] text-muted-foreground/40">
            IMPACT-X™ is proprietary to ArceusX Ventures
          </p>
        </div>
      </motion.div>
    </div>
  )
}
